/**
 * Thin client for the AI proxy worker.
 *
 * Attaches the caller's Firebase ID token so the proxy can resolve the user's
 * effective AI config + key server-side. No provider key ever lives here.
 *
 * Configure the proxy URL via REACT_APP_AI_PROXY_URL (e.g. http://localhost:8787).
 */

import { auth } from '../../config/firebase';

const PROXY_URL = process.env.REACT_APP_AI_PROXY_URL;

function baseUrl() {
  if (!PROXY_URL) {
    throw new Error(
      'AI proxy not configured. Set REACT_APP_AI_PROXY_URL to your ai-proxy worker URL.'
    );
  }
  return PROXY_URL.replace(/\/$/, '');
}

async function authHeader() {
  const user = auth?.currentUser;
  if (!user) throw new Error('You must be signed in to use AI features.');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json', ...(await authHeader()) };
  const res = await fetch(`${baseUrl()}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON error body */
  }

  if (!res.ok) {
    const message = data?.message || data?.error || `AI request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.code = data?.error;
    throw err;
  }
  return data;
}

export const aiProxy = {
  /** Markdown property analysis → returns { text, provider, model }. */
  analyzeProperty: (property) => request('POST', '/analyze', { op: 'analyzeProperty', property }),
  /** Markdown comparison → returns { text, provider, model }. */
  compareProperties: (target, similar) =>
    request('POST', '/analyze', { op: 'compareProperties', target, similar }),
  /** Save a BYOK key (validated + encrypted server-side) → { hasKey, last4 }. */
  saveKey: (provider, apiKey, model) => request('POST', '/key', { provider, apiKey, model }),
  /** Clear the stored key. */
  clearKey: (provider) => request('DELETE', '/key', { provider }),
  /** Public list of providers + models (no auth required). */
  async listProviders() {
    const res = await fetch(`${baseUrl()}/providers`);
    if (!res.ok) throw new Error(`Failed to load providers (${res.status})`);
    return res.json();
  },
};

export const isProxyConfigured = () => !!PROXY_URL;
